import React, { useContext, useMemo } from "react";
import Ctx from "../../contexts/AppContext";
import { crd, sp, rad } from "../../constants/styles";
import RevenueChart from "./RevenueChart";

/* ────────────────────────────────────────────────────────────────────────
   AnalyticsPage — top-right gear → Analytics
   Hosts the visual analytics widgets that used to sit on the dashboard
   (Revenue Trend chart, monthly KPIs, etc). The dashboard now stays
   focused on operational status (cash due, properties, agreements,
   property cards), while this page is the read-only insights view.
   ──────────────────────────────────────────────────────────────────── */
export default function AnalyticsPage() {
  const { t, h, go, pays, props } = useContext(Ctx);

  // Roll-up KPIs derived from the same `pays` array RevenueChart uses,
  // so the page hangs together as a coherent "this is your performance"
  // story instead of a single orphaned bar chart.
  const kpis = useMemo(() => {
    const mNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const now    = new Date();
    const last6  = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      last6.push(`${mNames[d.getMonth()]} ${d.getFullYear().toString().slice(-2)}`);
    }
    const inWindow = pays.filter(p => last6.includes(p.mo));
    const collected = inWindow.filter(p => p.st === "paid").reduce((s, p) => s + (p.amt || 0) + (p.com || 0), 0);
    const due       = inWindow.filter(p => p.st === "due" && !p.pending).reduce((s, p) => s + (p.amt || 0) + (p.com || 0), 0);
    const expected  = collected + due;
    const rate      = expected > 0 ? Math.round((collected / expected) * 100) : 0;
    const avgMonth  = Math.round(collected / 6);
    return { collected, due, expected, rate, avgMonth };
  }, [pays]);

  const occupied = props.filter(p => p.occ).length;
  const occRate  = props.length > 0 ? Math.round((occupied / props.length) * 100) : 0;

  /* ── Small KPI tile used inside the 2×2 grid ──────────────────────── */
  const KpiTile = ({ label, value, sub, color }) => (
    <div style={{
      background: h.cd, borderRadius: rad.lg,
      border: `1px solid ${h.bd}`, boxShadow: h.sh,
      padding: 14, minWidth: 0,
    }}>
      <div style={{ color: h.ts || h.sc, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ color: color || h.tx, fontSize: 18, fontWeight: 700, lineHeight: 1.1 }}>
        {value}
      </div>
      {sub && (
        <div style={{ color: h.ts || h.sc, fontSize: 11, marginTop: 4 }}>{sub}</div>
      )}
    </div>
  );

  return (
    <div style={{ paddingTop: 16 }}>
      {/* Back row */}
      <button
        onClick={() => go("dash")}
        style={{
          background: "none", border: "none", color: h.ac,
          cursor: "pointer", fontSize: 13, marginBottom: 8,
          padding: 0, display: "inline-flex", alignItems: "center", gap: 4,
        }}
      >
        {"←"} {t.back || "Back"}
      </button>

      {/* Page title */}
      <div style={{ marginBottom: sp.lg }}>
        <h2 style={{ color: h.tx, fontSize: 20, fontWeight: 700, margin: 0, letterSpacing: "-0.2px" }}>
          Analytics
        </h2>
        <div style={{ color: h.ts || h.sc, fontSize: 12, marginTop: 2 }}>
          Performance insights for the last 6 months
        </div>
      </div>

      {/* Revenue trend chart — the centrepiece, now the page's hero */}
      <div style={{ marginBottom: sp.lg }}>
        <RevenueChart pays={pays} h={h} t={t} />
      </div>

      {/* KPI grid (2 × 2) */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: sp.md, marginBottom: sp.lg }}>
        <KpiTile
          label="Collection rate"
          value={`${kpis.rate}%`}
          sub={`₹${kpis.collected.toLocaleString()} / ₹${kpis.expected.toLocaleString()}`}
          color={kpis.rate >= 80 ? h.gn : kpis.rate >= 50 ? h.wn : h.rd}
        />
        <KpiTile
          label="Outstanding"
          value={`₹${kpis.due.toLocaleString()}`}
          sub={kpis.due > 0 ? "Across past 6 months" : "Nothing pending"}
          color={kpis.due > 0 ? h.rd : h.gn}
        />
        <KpiTile
          label="Avg monthly income"
          value={`₹${kpis.avgMonth.toLocaleString()}`}
          sub="Last 6 months"
          color={h.ac}
        />
        <KpiTile
          label="Occupancy"
          value={`${occRate}%`}
          sub={`${occupied} of ${props.length} occupied`}
          color={occRate >= 80 ? h.gn : occRate >= 50 ? h.wn : h.rd}
        />
      </div>

      {/* Pointer to Reports (CSV exports) for those who want raw data */}
      <button
        onClick={() => go("reports")}
        style={{
          width: "100%",
          background: `${h.ac}10`, color: h.ac,
          border: `1px solid ${h.ac}30`,
          padding: "12px 14px", borderRadius: rad.md,
          cursor: "pointer", fontSize: 13, fontWeight: 600,
          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          transition: "background 0.15s",
        }}
        onMouseOver={e => { e.currentTarget.style.background = `${h.ac}1A`; }}
        onMouseOut={e  => { e.currentTarget.style.background = `${h.ac}10`; }}
      >
        Open Reports for CSV exports
        <span style={{ fontSize: 15, fontWeight: 700 }}>{"→"}</span>
      </button>
    </div>
  );
}
